import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLVideo = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Audio & Video</div>
                <h1>Video & Video Tags</h1>
                <p className="hero-lead">
                    Embed and control videos on your web pages using HTML5's native video element.
                    Learn about formats, accessibility, responsive video, and best practices.
                </p>
            </div>

            <div className="content-section">
                <h2>The &lt;video&gt; Element</h2>
                <p>
                    The <code>&lt;video&gt;</code> element embeds video content natively in HTML,
                    without requiring plugins like Flash.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Video Syntax</span>
                    </div>
                    <CodeBlock language="html" code={`<video controls>
    <source src="video.mp4" type="video/mp4">
    <source src="video.webm" type="video/webm">
    Your browser doesn't support video playback.
</video>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Video Attributes</h2>

                <h3>1. <code>controls</code> - Show Player Controls</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- With controls (play, pause, volume, etc.) --&gt;
<video src="video.mp4" controls></video>

<!-- Without controls (requires JavaScript control) --&gt;
<video src="video.mp4"></video>`} />
                </div>

                <h3>2. <code>autoplay</code> - Auto-start Video</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Autoplay (needs muted for most browsers) --&gt;
<video src="video.mp4" autoplay muted></video>`} />
                </div>

                <Warning><strong> Autoplay Restrictions:</strong> Most browsers block autoplay with sound.
                    Videos must be muted (<code>muted</code>) to autoplay.</Warning>

                <h3>3. <code>loop</code> - Repeat Video</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Loop indefinitely --&gt;
<video src="video.mp4" loop muted autoplay></video>`} />
                </div>

                <h3>4. <code>muted</code> - Start Muted</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Start with audio muted --&gt;
<video src="video.mp4" controls muted></video>`} />
                </div>

                <h3>5. <code>poster</code> - Thumbnail Image</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Show poster image before playback --&gt;
<video src="video.mp4" controls poster="thumbnail.jpg"></video>`} />
                </div>

                <h3>6. <code>preload</code> - Loading Strategy</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Don't preload (save bandwidth) --&gt;
<video src="video.mp4" controls preload="none"></video>

<!-- Preload metadata only (duration, dimensions) --&gt;
<video src="video.mp4" controls preload="metadata"></video>

<!-- Preload entire video (default) --&gt;
<video src="video.mp4" controls preload="auto"></video>`} />
                </div>

                <div className="preload-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Value</th>
                                <th>Behavior</th>
                                <th>Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>none</code></td>
                                <td>Don't preload anything</td>
                                <td>Save bandwidth, user may not watch</td>
                            </tr>
                            <tr>
                                <td><code>metadata</code></td>
                                <td>Load metadata (duration, size)</td>
                                <td>Show duration, save bandwidth</td>
                            </tr>
                            <tr>
                                <td><code>auto</code></td>
                                <td>Preload entire video (default)</td>
                                <td>User likely to watch immediately</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>7. <code>width</code> and <code>height</code></h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Fixed dimensions --&gt;
<video src="video.mp4" controls width="640" height="360"></video>

<!-- Responsive with CSS (preferred) --&gt;
<video src="video.mp4" controls style="width: 100%; height: auto;"></video>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Video Formats</h2>
                <p>
                    Different browsers support different video formats. Provide multiple formats:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Multiple Source Formats</span>
                    </div>
                    <CodeBlock language="html" code={`<video controls width="640" height="360">
    <!-- MP4 (H.264) - Best compatibility --&gt;
    <source src="video.mp4" type="video/mp4">
    
    <!-- WebM (VP9) - Good compression, open source --&gt;
    <source src="video.webm" type="video/webm">
    
    <!-- Ogg (Theora) - Older open source format --&gt;
    <source src="video.ogg" type="video/ogg">
    
    <!-- Fallback message --&gt;
    Your browser doesn't support the video tag.
</video>`} />
                </div>

                <div className="formats-comparison">
                    <h3>Video Format Comparison:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Format</th>
                                <th>Extension</th>
                                <th>Codec</th>
                                <th>Browser Support</th>
                                <th>Quality</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>MP4</td>
                                <td>.mp4</td>
                                <td>H.264</td>
                                <td>Universal </td>
                                <td>Excellent</td>
                            </tr>
                            <tr>
                                <td>WebM</td>
                                <td>.webm</td>
                                <td>VP8/VP9</td>
                                <td>Chrome, Firefox, Edge</td>
                                <td>Excellent</td>
                            </tr>
                            <tr>
                                <td>Ogg</td>
                                <td>.ogg, .ogv</td>
                                <td>Theora</td>
                                <td>Firefox, Chrome</td>
                                <td>Good</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="tip-box">
                    <strong> Recommendation:</strong> Provide MP4 (H.264) for maximum compatibility,
                    plus WebM for better compression. MP4 should be listed first.
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Video</h2>

                <h3>CSS-Only Responsive</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<video src="video.mp4" controls></video>

<style>
video {
    max-width: 100%;
    height: auto;
    display: block;
}
</style>`} />
                </div>

                <h3>Maintain Aspect Ratio (16:9)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div class="video-container">
    <video src="video.mp4" controls></video>
</div>

<style>
.video-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    height: 0;
    overflow: hidden;
}

.video-container video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
</style>`} />
                </div>

                <div className="aspect-ratios">
                    <h4>Common Aspect Ratios:</h4>
                    <ul>
                        <li><strong>16:9</strong> (widescreen): <code>padding-bottom: 56.25%</code></li>
                        <li><strong>4:3</strong> (standard): <code>padding-bottom: 75%</code></li>
                        <li><strong>21:9</strong> (ultra-wide): <code>padding-bottom: 42.857%</code></li>
                        <li><strong>1:1</strong> (square): <code>padding-bottom: 100%</code></li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Subtitles & Captions</h2>
                <p>
                    Add accessibility with <code>&lt;track&gt;</code> element:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Video with Subtitles</span>
                    </div>
                    <CodeBlock language="html" code={`<video controls>
    <source src="video.mp4" type="video/mp4">
    
    <!-- Subtitles (translations) --&gt;
    <track 
        kind="subtitles" 
        src="subtitles-en.vtt" 
        srclang="en" 
        label="English"
    >
    <track 
        kind="subtitles" 
        src="subtitles-es.vtt" 
        srclang="es" 
        label="Espaol"
    >
    
    <!-- Captions (for deaf/hard-of-hearing) --&gt;
    <track 
        kind="captions" 
        src="captions-en.vtt" 
        srclang="en" 
        label="English Captions"
        default
    >
</video>`} />
                </div>

                <h3>Track Kinds:</h3>
                <ul>
                    <li><code>subtitles</code> - Translations of dialogue</li>
                    <li><code>captions</code> - Dialogue + sound effects (for accessibility)</li>
                    <li><code>descriptions</code> - Audio descriptions of visual content</li>
                    <li><code>chapters</code> - Chapter titles for navigation</li>
                    <li><code>metadata</code> - Metadata for scripts</li>
                </ul>

                <h3>WebVTT File Format (.vtt):</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>captions-en.vtt</span>
                    </div>
                    <CodeBlock language="html" code={`WEBVTT

00:00:00.000 --&gt; 00:00:03.000
Welcome to our video tutorial!

00:00:03.500 --&gt; 00:00:07.000
Today we'll learn about HTML video.

00:00:07.500 --&gt; 00:00:11.000
Let's get started with the basics.`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Background Video</h2>
                <p>
                    Create hero sections with background video:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Hero Section with Video Background</span>
                    </div>
                    <CodeBlock language="html" code={`<div class="hero">
    <video class="hero-video" autoplay muted loop playsinline>
        <source src="hero-video.mp4" type="video/mp4">
        <source src="hero-video.webm" type="video/webm">
    </video>
    
    <div class="hero-content">
        <h1>Welcome to Our Site</h1>
        <p>Innovative solutions for modern problems</p>
        <button>Learn More</button>
    </div>
</div>

<style>
.hero {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
}

.hero-video {
    position: absolute;
    top: 50%;
    left: 50%;
    min-width: 100%;
    min-height: 100%;
    width: auto;
    height: auto;
    transform: translate(-50%, -50%);
    z-index: -1;
    object-fit: cover;
}

.hero-content {
    position: relative;
    z-index: 1;
    text-align: center;
    color: white;
    padding: 2rem;
    margin-top: 30vh;
}

/* Mobile: use static image instead */
@media (max-width: 768px) {
    .hero-video {
        display: none;
    }
    
    .hero {
        background-image: url('hero-fallback.jpg');
        background-size: cover;
        background-position: center;
    }
}
</style>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Mobile Consideration:</strong> Background videos consume data and battery.
                        Consider using a static image on mobile devices.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Custom Video Controls (JavaScript)</h2>
                <p>
                    Build custom controls for more control over styling:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Custom Video Player</span>
                    </div>
                    <CodeBlock language="html" code={`<div class="video-player">
    <video id="myVideo">
        <source src="video.mp4" type="video/mp4">
    </video>
    
    <div class="controls">
        <button id="playPause">Play</button>
        <input type="range" id="seekBar" value="0" max="100">
        <button id="mute">Mute</button>
        <input type="range" id="volumeBar" min="0" max="100" value="100">
        <span id="currentTime">0:00</span> / <span id="duration">0:00</span>
        <button id="fullscreen">Fullscreen</button>
    </div>
</div>

<script>
const video = document.getElementById('myVideo');
const playPause = document.getElementById('playPause');
const seekBar = document.getElementById('seekBar');
const volumeBar = document.getElementById('volumeBar');
const muteBtn = document.getElementById('mute');
const fullscreenBtn = document.getElementById('fullscreen');

// Play/Pause
playPause.addEventListener('click', () => {
    if (video.paused) {
        video.play();
        playPause.textContent = 'Pause';
    } else {
        video.pause();
        playPause.textContent = 'Play';
    }
});

// Seek
seekBar.addEventListener('input', () => {
    const time = video.duration * (seekBar.value / 100);
    video.currentTime = time;
});

video.addEventListener('timeupdate', () => {
    const value = (video.currentTime / video.duration) * 100;
    seekBar.value = value;
    
    // Update time display
    document.getElementById('currentTime').textContent = 
        formatTime(video.currentTime);
});

// Volume
volumeBar.addEventListener('input', () => {
    video.volume = volumeBar.value / 100;
});

// Mute
muteBtn.addEventListener('click', () => {
    video.muted = !video.muted;
    muteBtn.textContent = video.muted ? 'Unmute' : 'Mute';
});

// Fullscreen
fullscreenBtn.addEventListener('click', () => {
    if (video.requestFullscreen) {
        video.requestFullscreen();
    }
});

// Duration
video.addEventListener('loadedmetadata', () => {
    document.getElementById('duration').textContent = 
        formatTime(video.duration);
});

function formatTime(seconds) {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return \`\${min}:\${sec.toString().padStart(2, '0')}\`;
}
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Embedding YouTube/Vimeo Videos</h2>

                <h3>YouTube Embed</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Responsive YouTube embed --&gt;
<div class="video-container">
    <iframe 
        width="560" 
        height="315" 
        src="https://www.youtube.com/embed/VIDEO_ID"
        title="YouTube video player"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
    ></iframe>
</div>

<style>
.video-container {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
    height: 0;
}

.video-container iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
</style>`} />
                </div>

                <h3>Vimeo Embed</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div class="video-container">
    <iframe 
        src="https://player.vimeo.com/video/VIDEO_ID"
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen
    ></iframe>
</div>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Video Optimization</h2>

                <div className="optimization-tips">
                    <h3>Optimization Checklist:</h3>
                    <ul>
                        <li> <strong>Compress videos:</strong> Use tools like HandBrake, FFmpeg</li>
                        <li> <strong>Multiple formats:</strong> MP4 + WebM for best compatibility</li>
                        <li> <strong>Appropriate resolution:</strong> Don't serve 4K for mobile</li>
                        <li> <strong>Use poster images:</strong> Show thumbnail before playback</li>
                        <li> <strong>Lazy load:</strong> Use <code>preload="none"</code> or <code>preload="metadata"</code></li>
                        <li> <strong>CDN hosting:</strong> Serve videos from CDN</li>
                        <li> <strong>Add captions:</strong> Essential for accessibility</li>
                        <li> <strong>Responsive sizing:</strong> Scale for different screens</li>
                    </ul>
                </div>

                <h3>Recommended Settings:</h3>
                <div className="settings-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Device</th>
                                <th>Resolution</th>
                                <th>Bitrate</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Mobile</td>
                                <td>720p (1280720)</td>
                                <td>1-2 Mbps</td>
                            </tr>
                            <tr>
                                <td>Tablet</td>
                                <td>1080p (19201080)</td>
                                <td>3-5 Mbps</td>
                            </tr>
                            <tr>
                                <td>Desktop</td>
                                <td>1080p-4K</td>
                                <td>5-10 Mbps</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Provide multiple video formats</li>
                            <li>Add subtitles/captions for accessibility</li>
                            <li>Use poster images</li>
                            <li>Optimize video file sizes</li>
                            <li>Make videos responsive</li>
                            <li>Include controls attribute</li>
                            <li>Use preload wisely</li>
                            <li>Test on multiple browsers/devices</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Autoplay with sound (blocked by browsers)</li>
                            <li>Use only one video format</li>
                            <li>Serve huge uncompressed videos</li>
                            <li>Forget accessibility (captions)</li>
                            <li>Assume autoplay will work</li>
                            <li>Use background video on mobile</li>
                            <li>Forget poster images</li>
                            <li>Preload all videos on page</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLVideo;



